import { RouteComponentProps, useLocation } from '@reach/router';
import { ResourcesApi } from 'api/resourcesApi';
import ResourcePageStatus from 'components/resources/ResourcePageStatus';
import {
  isPageData,
  ResourcesRouteParams,
  toId,
  usePageTitle,
  useResourcesPage,
} from 'components/resources/resourcesHelpers';
import SubjectSiteContainerFixed from 'components/subjectSite/SubjectSiteContainerFixed';
import SubjectSiteLayout from 'components/subjectSite/SubjectSiteLayout';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';

type Props = RouteComponentProps<ResourcesRouteParams>;

const PLAYED_IN_PAGE = ['Video', 'Audio'];

const AudioPlayer: React.FC<{ src: string }> = ({ src }) => {
  const [bLoop, setBLoop] = useState(true);

  const toggleLoop = (event: React.SyntheticEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setBLoop(!bLoop);
  };

  // A button answers Space as well as Enter
  const onKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === ' ') toggleLoop(event);
  };

  return (
    <div className="AudioBtnTable">
      <div className="BtnCell">
        <div className="LoopBtn">
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="#" role="button" onClick={toggleLoop} onKeyDown={onKeyDown}>
            Loop: <span className="LoopToggle">{bLoop ? 'On' : 'Off'}</span>
          </a>
        </div>
      </div>
      <div className="AudioCell">
        <div className="PlayerParent">
          <audio src={src} controls autoPlay loop={bLoop} />
        </div>
      </div>
    </div>
  );
};

/**
 * Plays a video or audio resource, or sends the browser to any other file.
 * Loading it counts a view.
 */
const ResourcePlayer: React.FC<Props> = ({ subject: subjectCode }) => {
  const { search } = useLocation();
  const resourcesId = toId(queryString.parse(search).id);
  const result = useResourcesPage(
    subjectCode,
    resourcesId === null
      ? null
      : (subjectsId) => ResourcesApi.getFile(subjectsId, resourcesId, false),
    String(resourcesId)
  );
  const file = isPageData(result) ? result.details : null;
  const bPlayedInPage =
    file !== null && PLAYED_IN_PAGE.includes(file.resourceType);
  usePageTitle(
    isPageData(result)
      ? `${result.subject.name} - ${result.details.name}`
      : null
  );

  useEffect(() => {
    if (file && !bPlayedInPage) window.location.replace(file.filePath);
  }, [file, bPlayedInPage]);

  if (!isPageData(result) || !bPlayedInPage) {
    return <ResourcePageStatus result={isPageData(result) ? null : result} />;
  }

  const { details } = result;

  return (
    <SubjectSiteLayout colorCss="Blue_GSFPE">
      <SubjectSiteContainerFixed className="AVPage">
        <div className="WidthContent">
          <div className="Top">
            <h1 className="Title">{details.name}</h1>
          </div>
          <div className="Content">
            {details.resourceType === 'Video' ? (
              <div className="PlayerParent">
                <video
                  width="160"
                  height="90"
                  src={details.filePath}
                  controls
                  autoPlay
                />
              </div>
            ) : (
              <AudioPlayer src={details.filePath} />
            )}
          </div>
        </div>
      </SubjectSiteContainerFixed>
    </SubjectSiteLayout>
  );
};

export default ResourcePlayer;
